import React from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, RefreshCw } from 'lucide-react';
import type { RecaptchaError } from '../utils/recaptcha';

interface SecurityCheckProps {
  isLoading: boolean;
  error: RecaptchaError | null;
}

export default function SecurityCheck({ isLoading, error }: SecurityCheckProps) {
  const { t } = useTranslation('securityCheck');

  if (isLoading) {
    console.log('SecurityCheck: Loading state');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full animate-fade-in">
          <div className="flex flex-col items-center text-center">
            <div className="bg-blue-100 p-3 rounded-full mb-4">
              <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('title')}
            </h1>
            <p className="text-gray-600">
              {t('description')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    console.error('SecurityCheck: Error state', error);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full animate-fade-in">
          <div className="flex flex-col items-center text-center">
            <div className="bg-red-100 p-3 rounded-full mb-4">
              <Shield className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('error.title')}
            </h1>
            <p className="text-gray-600 mb-6">
              {t(`error.recaptcha.${error.code}`, { 
                defaultValue: t('error.message')
              })}
            </p>
            <div className="space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {error.code === 'load_failed' 
                  ? t('actions.refresh')
                  : t('actions.retry')
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  console.log('SecurityCheck: No loading or error state, rendering null');
  return null;
}